// dependency-graph-ignore-file

import type { FireParams, MetricConfig, MetricMetadataBase } from './define-metric';
import { runStabilizedScheduler, type MetricRef } from './stabilized-scheduler';

export { createMetricRef, cancelMetric, type MetricRef } from './stabilized-scheduler';

/**
 * Fires a metric using the stabilized probe model.
 *
 * Merges config-level metadata with ad-hoc metadata, builds the probe from config,
 * and starts the rAF-based stabilization loop. Use `useMetric` in React components.
 *
 * Pass `metadata.contentId` when you want database size (`entriesCount`, `fieldsCount`)
 * attached to the published payload.
 */
export function fireMetric<
  TData,
  TMetadata extends MetricMetadataBase = MetricMetadataBase
>(
  config: MetricConfig<TData, TMetadata>,
  params: FireParams<TData, TMetadata>,
  ref: MetricRef
): void {
  if (params.skipIfInFlight && ref.current !== null) {
    return;
  }

  const { startTime, probeData, metadata, overrides, failImmediately } = params;
  const configMeta =
    metadata !== undefined ? config.buildMetadata?.(metadata) : undefined;
  const mergedMeta = { ...configMeta, ...metadata };

  runStabilizedScheduler(
    {
      startTime,
      metricName: config.metricName,
      getFrameState: config.buildProbe(probeData),
      metadata: mergedMeta,
      maxWaitMs: overrides?.maxWaitMs ?? config.maxWaitMs,
      stableFramesRequired:
        overrides?.stableFramesRequired ?? config.stableFramesRequired,
      failImmediately,
    },
    ref
  );
}
